"use client"

import Link from "next/link"
import { usePathname, useSearchParams } from "next/navigation"
import { Check, Phone, UserRoundPlus } from "lucide-react"

import { AddToCalendar } from "@/components/marketing/add-to-calendar"
import { marketingActionClass } from "@/components/marketing/marketing-action"
import { useBookingGetsContactCard } from "@/components/marketing/save-our-number"
import { SITE_CONFIG } from "@/lib/site-config"

const JAKARTA_HEADING_FONT =
  'var(--font-jakarta), Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif'

export type YoureBookedProps = {
  /** Fallback booking values for a render with no booking in the URL — the
   *  workbench cell. On the live page Cal.com puts the real uid/start/end in
   *  the URL after bookingSuccessful, and those win. */
  uid: string | null
  start: string | null
  end: string | null
}

/** Whole-minute call length from the booking's own start/end, or null when
 *  either time is missing or unparseable, so the suffix never guesses. */
export function callMinutes(start: string | null, end: string | null): number | null {
  if (!start || !end) return null
  const startMs = new Date(start).getTime()
  const endMs = new Date(end).getTime()
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return null
  return Math.round((endMs - startMs) / 60000)
}

export function formatWhen(start: string | null): string | null {
  if (!start) return null
  const date = new Date(start)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date)
}

/** The business line written the way the mock sets it: +1 929-209-7870. Split
 *  from `SITE_CONFIG.phone`, the one place the line is held, so a changed
 *  number can never leave this panel on the old one. */
function businessNumberText(e164: string): string | null {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164)
  return m ? `+1 ${m[1]}-${m[2]}-${m[3]}` : null
}

/** The booked confirmation hero of /what-happens-next: crimson check circle,
 *  the "You're booked." heading, the bold when line, the confirmation-inbox
 *  note, the coach paragraph, the training lead-in, AddToCalendar, and the
 *  reschedule link demoted to small muted text. For a booking whose coach
 *  rings from the business line, the number sits under the when line and Save
 *  our number pairs with Add to calendar, the reason under the pair. One
 *  markup source for the page and the cell. */
export function YoureBooked(props: YoureBookedProps) {
  const searchParams = useSearchParams()
  // The representative fallback props exist for the WORKBENCH cell only. On
  // the live page the URL is the sole source of booking data: a bare or
  // shared URL hides the when line and the calendar button rather than
  // showing a date the visitor never booked.
  const onLivePage = usePathname() === "/what-happens-next"
  const uid = searchParams.get("uid") ?? (onLivePage ? null : props.uid)
  const start = searchParams.get("start") ?? (onLivePage ? null : props.start)
  const end = searchParams.get("end") ?? (onLivePage ? null : props.end)
  const when = formatWhen(start)
  const minutes = callMinutes(start, end)
  // The workbench cell stands for an ordinary application call from the
  // business line; the live page waits for the booking lookup to say so.
  const showSave = useBookingGetsContactCard(uid, !onLivePage)
  const number = showSave ? businessNumberText(SITE_CONFIG.phone) : null

  return (
    <div className="text-center">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-vermilion)] text-white">
        <Check size={23} strokeWidth={2.25} aria-hidden />
      </span>
      <h1
        style={{ fontFamily: JAKARTA_HEADING_FONT, fontWeight: 800, letterSpacing: "-0.045em" }}
        className="mt-6 text-balance text-[clamp(1.9rem,4vw,2.6rem)] leading-[1.08] text-[var(--color-ink)]"
      >
        You&apos;re booked.
      </h1>
      {when ? (
        <p className="mt-3.5 text-[1.125rem] font-bold text-[var(--color-ink)] md:text-[1.25rem]">
          {when}
          {minutes ? ` · ${minutes} minutes` : ""}
        </p>
      ) : null}
      {number ? (
        <p className="mt-2 flex items-center justify-center gap-[7px] text-[0.9375rem] font-semibold leading-[1.5] text-[var(--color-ink)]">
          <Phone size={15} aria-hidden className="text-[var(--color-ink-soft)]" />
          <span className="whitespace-nowrap [font-variant-numeric:tabular-nums]">{number}</span>
        </p>
      ) : null}
      <p className="mx-auto mt-2 max-w-[42rem] text-[0.875rem] leading-[1.6] text-[var(--color-ink-muted)]">
        A confirmation is on its way to your inbox.
      </p>
      <p className="mx-auto mt-5 max-w-[42rem] text-[1rem] leading-[1.65] text-[var(--color-ink-muted)]">
        Your coach will use this call to understand where you&apos;re at, what&apos;s getting in the way, and
        whether one of our programs makes sense for you.
      </p>
      <div className="isolate mt-6 flex flex-col items-center gap-3">
        {number ? (
          // Two equal columns of one width and height, one filled and one
          // outlined; one column under 460px. Rows start at the top so the
          // calendar menu opening under its button moves nothing beside it.
          <div className="grid w-full max-w-[404px] grid-cols-1 items-start gap-3 min-[460px]:grid-cols-2 [&>div>button]:w-full">
            <AddToCalendar uid={uid} fallbackStart={start} fallbackEnd={end} />
            <a
              href="/api/contact-card"
              download="cold-approach-contact.vcf"
              className={marketingActionClass({ variant: "primary", size: "md", className: "w-full" })}
            >
              <UserRoundPlus size={16} aria-hidden />
              Save our number
            </a>
          </div>
        ) : (
          <AddToCalendar uid={uid} fallbackStart={start} fallbackEnd={end} />
        )}
        {number ? (
          <p className="m-0 text-[0.8125rem] leading-[1.5] text-[var(--color-ink-muted)]">
            Many phones silence unknown callers.
          </p>
        ) : null}
        <Link
          href="/book"
          // A 44px tap area with nothing on screen moving: an invisible after:
          // box centred on the words takes the tap, so the link's own box, and
          // the focus ring drawn around it, still hug the words. -z-1 inside
          // the isolated column keeps the words painting under the Add to
          // calendar button's shadow, as they did before the tap area.
          className="relative -z-1 text-[0.8125rem] text-[var(--color-ink-muted)] underline underline-offset-4 transition-colors after:absolute after:inset-x-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-[''] hover:text-[var(--color-ink)]"
        >
          Can&apos;t make it? Reschedule here.
        </Link>
      </div>
    </div>
  )
}
